import { useEffect, useState, useMemo } from 'react';
import { BaseContext } from './shared/base-context';
import { Landing } from './routes/landing/landing';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { Admin } from './routes/admin/admin';
import { Main } from './routes/main/main';
import { MainRoutes } from './shared/models';
import { Assistants } from './routes/admin/routes/assistants/assistants';
import { useConvexAuth } from 'convex/react';
import { Chat } from './routes/chat/chat';
import { AdminRoute } from './routes/AdminRoute';
import { AddAssistant } from './routes/admin/routes/assistants/add-assistant';
import { AssistantTypes } from './routes/admin/routes/assistants/assistant-types';
import { Progress } from './routes/progress/progress';
import { TraitDetails } from './routes/progress/components/TraitDetails';
import { BrowseAll } from './routes/progress/components/BrowseAll';
import { Skills as MySkills } from './routes/skills/skills';
import { AuthenticatedRoute } from './routes/AuthenticatedRoute';
import { ConditionalRoute } from './routes/ConditionalRoute';
import { ManageProgress } from './routes/admin/routes/progress/manage-progress';
import { SystemAssistants } from './routes/admin/routes/system/system-assistants';
import { ViewSystemAssistant } from './routes/admin/routes/system/view-system-assistant';
import { GlobalEmbeddingsGrid } from './routes/admin/routes/context/GlobalEmbeddingsGrid';
import { EmbeddingsConfig } from './routes/admin/routes/context/EmbeddingsConfig';
import { AssistantAdminMetadata } from './routes/admin/routes/system/assistant-admin-metadata';
import { ManualContextUpload } from './routes/admin/routes/ManualContextUpload';
import { Documentation } from './routes/admin/routes/documentation';
import { KnowledgeBase } from './routes/admin/routes/knowledge-base';
import { KnowledgeBaseAnalyticsRoute } from './routes/admin/routes/knowledge-base-analytics';
import { Subscribers } from './routes/admin/routes/subscribers/subscribers';
import { SessionAnalytics, SessionInsights, AssistantSessionsDetail } from './routes/admin/routes/sessions';
import { SessionDetail } from './routes/admin/routes/sessions/SessionDetail';
import { UserProgress } from './routes/admin/routes/progress';
import { UsersPage } from './routes/admin/users-page';
import { UserDetail } from './routes/admin/routes/UserDetail';
import { Profile } from './routes/profile/Profile';
import { Subscribe } from './routes/subscribe/subscribe';
import { Unsubscribe } from './routes/unsubscribe/unsubscribe';
import { ToastProvider } from './shared/components/ToastContainer';
import { Legal } from './routes/admin/routes/legal/legal';
import { LegalAcceptance } from './routes/legal-acceptance/legal-acceptance';
import { Skills } from './routes/admin/routes/skills/skills';
import { SkillDetails } from './routes/admin/routes/skills/skill-details';
import { EvergreenChat } from './routes/evergreen-chat/evergreen-chat';
import { CheckinInstructions } from './routes/admin/routes/checkins';
import { LevelConfig } from './routes/admin/routes/level-config/level-config';
import { AcornConfig } from './routes/admin/routes/acorn-config/acorn-config';
import { AdminFeedback, FeedbackDetail } from './routes/admin/routes/feedback';
import { IntroSlides } from './routes/admin/routes/intro-slides';
import { Tools, ToolDetail } from './routes/admin/routes/tools';
import { InnerWeatherQuotes } from './routes/admin/routes/inner-weather-quotes';
import { FAQ } from './routes/faq/faq';
import { AdminFAQ } from './routes/admin/routes/faq';
import { UserAnalytics } from './routes/admin/routes/user-analytics';
import { EngagementAnalytics } from './routes/admin/routes/engagement-analytics';
import { ScreenTimeAnalytics } from './routes/admin/routes/screen-time-analytics';
import { AuditLog, AuditLogDetail } from './routes/admin/routes/audit-log';
import { NotFound } from './routes/not-found/not-found';
import { RouteErrorBoundary } from './shared/components/RouteErrorBoundary';
import { useUser } from '@clerk/clerk-react';

const router = createBrowserRouter([
  {
    path: MainRoutes.Subscribe,
    element: <Subscribe />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: MainRoutes.Unsubscribe,
    element: <Unsubscribe />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/",
    element: <Main />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: MainRoutes.Landing,
        element: <Landing />,
      },
      {
        path: MainRoutes.Chat,
        element: <AuthenticatedRoute><Chat /></AuthenticatedRoute>,
      },
      {
        path: MainRoutes.Progress,
        element: <AuthenticatedRoute><Progress /></AuthenticatedRoute>,
      },
      {
        path: MainRoutes.TraitDetails,
        element: <AuthenticatedRoute><TraitDetails /></AuthenticatedRoute>,
      },
      {
        path: MainRoutes.Skills,
        element: <AuthenticatedRoute><MySkills /></AuthenticatedRoute>,
      },
      {
        path: MainRoutes.BrowseAll,
        element: <AuthenticatedRoute><BrowseAll /></AuthenticatedRoute>,
      },
      {
        path: MainRoutes.PersonalAssistant,
        element: (
          <AuthenticatedRoute>
            <ConditionalRoute conditionKey="IsPersonalAssistantEnabled" redirectTo={MainRoutes.Progress}>
              <Chat />
            </ConditionalRoute>
          </AuthenticatedRoute>
        ),
      },
      {
        path: MainRoutes.Profile,
        element: <AuthenticatedRoute><Profile /></AuthenticatedRoute>,
      },
      {
        path: MainRoutes.LegalAcceptance,
        element: <LegalAcceptance />,
      },
      {
        path: MainRoutes.MyChat,
        element: <AuthenticatedRoute><EvergreenChat /></AuthenticatedRoute>,
      },
      {
        path: MainRoutes.FAQ,
        element: <AuthenticatedRoute><FAQ /></AuthenticatedRoute>,
      },
      {
        path: MainRoutes.Admin,
        children: [
          {
            element: <AdminRoute><Admin /></AdminRoute>,
            index: true,
          },
          {
            path: 'assistants',
            children: [
              {
                element: <AdminRoute><Assistants /></AdminRoute>,
                index: true
              },
              {
                path: 'new',
                element: <AdminRoute><AddAssistant /></AdminRoute>
              },
              {
                path: ':assistantId',
                element: <AdminRoute><AddAssistant /></AdminRoute>,
              }
            ]
          },
          {
            path: 'skills',
            children: [
              {
                element: <AdminRoute><Skills /></AdminRoute>,
                index: true
              },
              {
                path: ':id',
                element: <AdminRoute><SkillDetails /></AdminRoute>
              }
            ]
          },
           {
             path: 'legal',
             element: <AdminRoute><Legal /></AdminRoute>
           },
          {
            path: 'types',
            element: <AdminRoute><AssistantTypes /></AdminRoute>
          },
          {
            path: 'progress',
            element: <AdminRoute><ManageProgress /></AdminRoute>
          },
          {
            path: 'metadata',
            element: <AdminRoute><AssistantAdminMetadata /></AdminRoute>
          },
          {
            path: 'system',
            children: [
              {
                element: <AdminRoute><SystemAssistants /></AdminRoute>,
                index: true
              },
              {
                path: ':assistantId',
                element: <AdminRoute><ViewSystemAssistant /></AdminRoute>
              },
            ]
          },
          {
            path: 'context',
            element: <AdminRoute><GlobalEmbeddingsGrid /></AdminRoute>
          },
          {
            path: 'manual-context-upload',
            element: <AdminRoute><ManualContextUpload /></AdminRoute>
          },
          {
            path: 'embeddings-config',
            element: <AdminRoute><EmbeddingsConfig /></AdminRoute>
          },
          {
            path: 'documentation',
            element: <AdminRoute><Documentation /></AdminRoute>
          },
          {
            path: 'knowledge-base',
            children: [
              {
                element: <AdminRoute><KnowledgeBase /></AdminRoute>,
                index: true
              },
              {
                path: 'analytics',
                element: <KnowledgeBaseAnalyticsRoute />
              }
            ]
          },
          {
            path: 'subscribers',
            element: <AdminRoute><Subscribers /></AdminRoute>
          },
          {
            path: 'sessions',
            children: [
              {
                element: <AdminRoute><SessionAnalytics /></AdminRoute>,
                index: true
              },
              {
                path: 'assistant/:assistantId',
                element: <AdminRoute><AssistantSessionsDetail /></AdminRoute>
              },
              {
                path: ':sessionId',
                element: <AdminRoute><SessionDetail /></AdminRoute>
              },
              {
                path: ':sessionId/insights',
                element: <AdminRoute><SessionInsights /></AdminRoute>
              }
            ]
          },
          {
            path: 'user-progress',
            element: <AdminRoute><UserProgress /></AdminRoute>
          },
          {
            path: 'users',
            element: <AdminRoute><UsersPage /></AdminRoute>
          },
          {
            path: 'users/:userId',
            element: <AdminRoute><UserDetail /></AdminRoute>
          },
          {
            path: 'checkin-instructions',
            element: <AdminRoute><CheckinInstructions /></AdminRoute>
          },
          {
            path: 'level-config',
            element: <AdminRoute><LevelConfig /></AdminRoute>
          },
          {
            path: 'acorn-config',
            element: <AdminRoute><AcornConfig /></AdminRoute>
          },
          {
            path: 'feedback',
            children: [
              {
                index: true,
                element: <AdminRoute><AdminFeedback /></AdminRoute>
              },
              {
                path: ':feedbackId',
                element: <AdminRoute><FeedbackDetail /></AdminRoute>
              }
            ]
          },
          {
            path: 'intro-slides',
            element: <AdminRoute><IntroSlides /></AdminRoute>
          },
          {
            path: 'tools',
            children: [
              {
                index: true,
                element: <AdminRoute><Tools /></AdminRoute>
              },
              {
                path: ':id',
                element: <AdminRoute><ToolDetail /></AdminRoute>
              }
            ]
          },
          {
            path: 'inner-weather-quotes',
            element: <AdminRoute><InnerWeatherQuotes /></AdminRoute>
          },
          {
            path: 'faq',
            element: <AdminRoute><AdminFAQ /></AdminRoute>
          },
          {
            path: 'user-analytics',
            element: <AdminRoute><UserAnalytics /></AdminRoute>
          },
          {
            path: 'engagement-analytics',
            element: <AdminRoute><EngagementAnalytics /></AdminRoute>
          },
          {
            path: 'screen-time-analytics',
            element: <AdminRoute><ScreenTimeAnalytics /></AdminRoute>
          },
          {
            path: 'audit-log',
            children: [
              {
                index: true,
                element: <AdminRoute><AuditLog /></AdminRoute>
              },
              {
                path: ':entryId',
                element: <AdminRoute><AuditLogDetail /></AdminRoute>
              }
            ]
          }
        ]
      },
      {
        path: '*',
        element: <NotFound />
      }
    ]
  }
]);

export const App = () => {
  const [isSidebarShown, setIsSidebarShown] = useState(false);
  const [convexUserId, setConvexUserId] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [debug, setDebug] = useState(false);
  const { isAuthenticated, isLoading } = useConvexAuth();
  const { user } = useUser();

  const isAuthenticationComplete = !isLoading;

  // Determine admin status from Clerk publicMetadata
  useEffect(() => {
    if (user) {
      const adminFromMetadata = (user.publicMetadata as { role?: string })?.role === 'admin';
      setIsAdmin(adminFromMetadata);
    }
  }, [user]);

  // Clear convexUserId when auth session expires so queries stop firing
  useEffect(() => {
    if (!isLoading && !isAuthenticated && convexUserId) {
      setConvexUserId('');
    }
  }, [isLoading, isAuthenticated, convexUserId]);

  const context = useMemo(() => ({
    isSidebarShown,
    setIsSidebarShown,
    convexUserId,
    setConvexUserId,
    debug,
    setDebug,
    isAdmin,
    setIsAdmin,
    user,
    isAuthenticationComplete
  }), [
    isSidebarShown,
    setIsSidebarShown,
    convexUserId,
    setConvexUserId,
    debug,
    setDebug,
    isAdmin,
    setIsAdmin,
    user,
    isAuthenticationComplete
  ]);

  return (
    <BaseContext.Provider value={context}>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </BaseContext.Provider>
  );
};
